import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DistributedCoupon, FlyoutDataType, FlyoutOpeningData, PersonDetails, SessionDetails } from '../flyout-models';
import { FlyoutService } from '../flyout.service';

@Component({
  selector: 'app-flyout-distributed-coupons',
  templateUrl: './flyout-distributed-coupons.component.html',
  styleUrls: ['./flyout-distributed-coupons.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutDistributedCouponsComponent implements OnInit, OnDestroy {
  public coupons: Array<DistributedCoupon> = [];
  private flyoutOpeningData: FlyoutOpeningData;
  private sessionDetails: SessionDetails;
  private personDetails: PersonDetails;
  private subscriptions: Subscription[] = [];

  constructor(private readonly flyoutService: FlyoutService) {}

  ngOnInit() {
    this.subscriptions.push(
      this.flyoutService.flyoutOpeningDataBS.subscribe((data: FlyoutOpeningData) => {
        this.flyoutOpeningData = data;
        this.recompute();
      }),
      this.flyoutService.sessionDetailsBS.subscribe((details: SessionDetails) => {
        this.sessionDetails = details;
        this.recompute();
      }),
      this.flyoutService.personDetailsBS.subscribe((details: PersonDetails) => {
        this.personDetails = details;
        this.recompute();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private recompute(): void {
    if (this.flyoutOpeningData?.type === FlyoutDataType.Session && this.sessionDetails) {
      this.coupons = this.sessionDetails.distributedCoupons ?? [];
    } else if (this.flyoutOpeningData?.type === FlyoutDataType.Contact && this.personDetails) {
      this.coupons = this.dedupeAcrossVisits(this.personDetails.visits ?? []);
    } else {
      this.coupons = [];
    }
  }

  // Profile view: flatten Visits[].distributedCoupons, keep one entry per (couponSetId, code) —
  // most recent wins on positioning, IsCopied is OR'd across all occurrences.
  private dedupeAcrossVisits(
    visits: Array<{ distributedCoupons?: Array<DistributedCoupon> }>,
  ): Array<DistributedCoupon> {
    const byKey = new Map<string, DistributedCoupon>();
    for (const visit of visits) {
      for (const coupon of visit.distributedCoupons ?? []) {
        const key = `${coupon.couponSetId ?? ''}|${coupon.code}`;
        const existing = byKey.get(key);
        if (existing) {
          existing.isCopied = existing.isCopied || coupon.isCopied;
        } else {
          byKey.set(key, { ...coupon });
        }
      }
    }
    return Array.from(byKey.values());
  }
}
